import React, { useState, useEffect } from 'react';
import { Download, ShoppingCart, Eye, X, Check } from 'lucide-react';
import { EventPhoto } from '../types';
import { getEventPhotos, getUserPurchasedPhotos, purchasePhotos, calculatePhotoTotal } from '../services/photoService';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';

interface PhotoGalleryProps {
  eventId: string;
  eventTitle: string;
}

const PhotoGallery: React.FC<PhotoGalleryProps> = ({ eventId, eventTitle }) => {
  const { user, isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const [photos, setPhotos] = useState<EventPhoto[]>([]);
  const [purchasedPhotos, setPurchasedPhotos] = useState<string[]>([]);
  const [selectedPhotos, setSelectedPhotos] = useState<string[]>([]);
  const [selectedPhoto, setSelectedPhoto] = useState<EventPhoto | null>(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseId, setPurchaseId] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<'idle' | 'processing' | 'completed' | 'failed'>('idle');
  const [showCart, setShowCart] = useState(false);

  useEffect(() => {
    const loadPhotos = async () => {
      try {
        setLoading(true);
        const eventPhotos = await getEventPhotos(eventId);
        setPhotos(eventPhotos);
        
        if (user) {
          const purchased = await getUserPurchasedPhotos(user.id, eventId);
          setPurchasedPhotos(purchased);
        }
      } catch (error) {
        console.error('Error loading photos:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPhotos();
  }, [eventId, user]);

  const togglePhotoSelection = (photoId: string) => {
    if (purchasedPhotos.includes(photoId)) return; // Can't select already purchased photos
    
    setSelectedPhotos(prev => 
      prev.includes(photoId) 
        ? prev.filter(id => id !== photoId)
        : [...prev, photoId]
    );
  };

  const handlePurchase = async () => {
    if (!user || selectedPhotos.length === 0) return;

    try {
      setPurchasing(true);
      setPaymentStatus('processing');
      
      const newPurchaseId = await purchasePhotos(user.id, eventId, selectedPhotos);
      setPurchaseId(newPurchaseId);
      
      // Poll for payment completion
      const checkPayment = setInterval(async () => {
        try {
          const { getPurchaseStatus } = await import('../services/photoService');
          const purchase = await getPurchaseStatus(newPurchaseId);
          
          if (purchase?.status === 'completed') {
            clearInterval(checkPayment);
            setPaymentStatus('completed');
            
            // Update purchased photos
            setPurchasedPhotos(prev => [...prev, ...selectedPhotos]);
            setSelectedPhotos([]);
            setShowCart(false);
            
            alert('Pagamento aprovado! Fotos liberadas para download.');
          } else if (purchase?.status === 'failed') {
            clearInterval(checkPayment);
            setPaymentStatus('failed');
            alert('Pagamento falhou. Tente novamente.');
          }
        } catch (error) {
          console.error('Error checking payment status:', error);
        }
      }, 1000);
      
      // Stop polling after 30 seconds
      setTimeout(() => {
        clearInterval(checkPayment);
        if (paymentStatus === 'processing') {
          setPaymentStatus('failed');
          alert('Timeout no pagamento. Tente novamente.');
        }
      }, 30000);
      
    } catch (error) {
      console.error('Error purchasing photos:', error);
      setPaymentStatus('failed');
      alert('Erro ao comprar fotos. Tente novamente.');
    } finally {
      setPurchasing(false);
    }
  };

  const downloadPhoto = (photo: EventPhoto) => {
    if (!purchasedPhotos.includes(photo.id)) return;
    
    // Create download link for original photo
    const link = document.createElement('a');
    link.href = photo.originalUrl;
    link.download = photo.filename;
    link.click();
  };

  const pricing = calculatePhotoTotal(selectedPhotos.length);

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-indigo-600 border-r-transparent"></div>
        <p className="mt-4 text-gray-600">Carregando fotos...</p>
      </div>
    );
  }

  if (photos.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Nenhuma foto disponível para este evento.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-2xl font-bold text-gray-800">
          Fotos do Evento ({photos.length})
        </h3>
        
        {isAuthenticated && selectedPhotos.length > 0 && (
          <button
            onClick={() => setShowCart(true)}
            className="bg-indigo-600 text-white px-4 py-2 rounded-md flex items-center hover:bg-indigo-700 transition-colors"
          >
            <ShoppingCart className="h-5 w-5 mr-2" />
            Carrinho ({selectedPhotos.length})
          </button>
        )}
      </div>

      {/* Photo Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {photos.map((photo) => {
          const isPurchased = purchasedPhotos.includes(photo.id);
          const isSelected = selectedPhotos.includes(photo.id);
          
          return (
            <div key={photo.id} className="relative group">
              <div className="aspect-square overflow-hidden rounded-lg bg-gray-100">
                <img
                  src={photo.thumbnailUrl}
                  alt={`Foto do evento ${eventTitle}`}
                  className="w-full h-full object-cover cursor-pointer transition-transform group-hover:scale-105"
                  onClick={() => setSelectedPhoto(photo)}
                />
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity flex space-x-2">
                    <button
                      onClick={() => setSelectedPhoto(photo)}
                      className="bg-white text-gray-800 p-2 rounded-full hover:bg-gray-100"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    
                    {isPurchased ? (
                      <button
                        onClick={() => downloadPhoto(photo)}
                        className="bg-green-600 text-white p-2 rounded-full hover:bg-green-700"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                    ) : isAuthenticated ? (
                      <button
                        onClick={() => togglePhotoSelection(photo.id)}
                        className={`p-2 rounded-full ${
                          isSelected 
                            ? 'bg-indigo-600 text-white' 
                            : 'bg-white text-gray-800 hover:bg-gray-100'
                        }`}
                      >
                        {isSelected ? <Check className="h-4 w-4" /> : <ShoppingCart className="h-4 w-4" />}
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
              
              {/* Status indicators */}
              <div className="absolute top-2 right-2 flex space-x-1">
                {isPurchased && (
                  <span className="bg-green-600 text-white text-xs px-2 py-1 rounded-full">
                    Comprada
                  </span>
                )}
                {isSelected && (
                  <span className="bg-indigo-600 text-white text-xs px-2 py-1 rounded-full">
                    Selecionada
                  </span>
                )}
              </div>
              
              {/* Price */}
              {!isPurchased && (
                <div className="absolute bottom-2 left-2">
                  <span className="bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
                    R$ {photo.price}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Photo Modal */}
      {selectedPhoto && (
        <>
          {photos.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 max-w-md">
              <p className="text-sm text-blue-800">
                <strong>ℹ️ Proteção Anti-Pirataria:</strong> Todas as fotos possuem marca d'água diagonal robusta. 
                Apenas versões compradas são liberadas sem marca d'água.
              </p>
            </div>
          )}
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-4xl max-h-full">
            <button
              onClick={() => setSelectedPhoto(null)}
              className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
            >
              <X className="h-8 w-8" />
            </button>
            
            <img
              src={selectedPhoto.watermarkedUrl}
              alt={`Foto do evento ${eventTitle}`}
              className="max-w-full max-h-full object-contain"
            />
            
            <div className="absolute bottom-4 left-4 right-4 bg-black bg-opacity-70 text-white p-4 rounded">
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium">Foto do evento {eventTitle}</p>
                  <p className="text-sm text-gray-300">
                    {purchasedPhotos.includes(selectedPhoto.id) ? 'Comprada' : `R$ ${selectedPhoto.price}`}
                  </p>
                </div>
                
                {purchasedPhotos.includes(selectedPhoto.id) ? (
                  <button
                    onClick={() => downloadPhoto(selectedPhoto)}
                    className="bg-green-600 text-white px-4 py-2 rounded-md flex items-center hover:bg-green-700"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </button>
                ) : isAuthenticated ? (
                  <button
                    onClick={() => togglePhotoSelection(selectedPhoto.id)}
                    className={`px-4 py-2 rounded-md flex items-center ${
                      selectedPhotos.includes(selectedPhoto.id)
                        ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                        : 'bg-white text-gray-800 hover:bg-gray-100'
                    }`}
                  >
                    <ShoppingCart className="h-4 w-4 mr-2" />
                    {selectedPhotos.includes(selectedPhoto.id) ? 'Remover' : 'Adicionar'}
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
        </>
      )}

      {/* Shopping Cart Modal */}
      {showCart && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">Carrinho de Fotos</h3>
              <button
                onClick={() => setShowCart(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="border-b pb-4">
                <p className="text-sm text-gray-600">
                  {selectedPhotos.length} foto{selectedPhotos.length !== 1 ? 's' : ''} selecionada{selectedPhotos.length !== 1 ? 's' : ''}
                </p>
                
                <div className="mt-2 space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Subtotal:</span>
                    <span>R$ {pricing.subtotal.toFixed(2)}</span>
                  </div>
                  
                  {pricing.discount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Desconto ({pricing.discount}%):</span>
                      <span>-R$ {pricing.discountAmount.toFixed(2)}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between font-bold text-lg border-t pt-1">
                    <span>Total:</span>
                    <span>R$ {pricing.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
              
              {pricing.discount > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-md p-3">
                  <p className="text-sm text-green-800">
                    🎉 Você está economizando {pricing.discount}% comprando {selectedPhotos.length} fotos!
                  </p>
                </div>
              )}
              
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowCart(false)}
                  className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-300"
                >
                  Continuar Comprando
                </button>
                <button
                  onClick={handlePurchase}
                  disabled={purchasing || paymentStatus === 'processing'}
                  className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center"
                >
                  {paymentStatus === 'processing' ? (
                    <>
                      <div className="h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Processando Pagamento...
                    </>
                  ) : purchasing ? (
                    'Iniciando Compra...'
                  ) : (
                    'Comprar Agora'
                  )}
                </button>
              </div>
              
              {paymentStatus === 'processing' && (
                <div className="mt-4 bg-blue-50 border border-blue-200 rounded-md p-3">
                  <p className="text-sm text-blue-800">
                    💳 Processando seu pagamento... Aguarde alguns segundos.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoGallery;